import React, { useEffect, useMemo, useState } from "react";
import { X, Save, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { createContact, updateContact, deleteContact } from "../../apis/contacts";
import { listCustomFields } from "../../apis/contactCustomFields";

/**
 * Slide-in drawer for create / edit contact. When `contact.id` is falsy,
 * it's a create form; otherwise an edit form.
 */
const ContactDrawer = ({ phoneId, contact, onClose, onSaved }) => {
  const [form, setForm] = useState(() => normalize(contact));
  const [customFields, setCustomFields] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listCustomFields()
      .then((r) => setCustomFields(r.data.fields || []))
      .catch(() => setCustomFields([]));
  }, []);

  useEffect(() => {
    setForm(normalize(contact));
  }, [contact]);

  const isEdit = useMemo(() => !!contact?.id, [contact]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setCustom = (key, value) =>
    setForm((f) => ({ ...f, custom_fields: { ...(f.custom_fields || {}), [key]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone) {
      toast.error("Phone is required");
      return;
    }
    if (!String(form.country_code || "").replace(/\D/g, "")) {
      toast.error("country code can not be empty");
      return;
    }
    setSaving(true);
    try {
      const payload = { phone_id: phoneId, ...form };
      // Drop empty custom field keys to keep storage clean
      if (payload.custom_fields) {
        const cf = {};
        for (const [k, v] of Object.entries(payload.custom_fields)) {
          if (v !== "" && v !== null && v !== undefined) cf[k] = v;
        }
        payload.custom_fields = cf;
      }
      if (form.tags && typeof form.tags === "string") {
        payload.tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      }
      if (isEdit) {
        await updateContact(contact.id, payload);
        toast.success("Contact updated");
      } else {
        await createContact(payload);
        toast.success("Contact created");
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this contact?")) return;
    try {
      await deleteContact(contact.id, phoneId);
      toast.success("Contact deleted");
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 bottom-0 w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {isEdit ? "Edit contact" : "New contact"}
            </h3>
            {isEdit && (
              <p className="text-xs text-gray-500 mt-0.5">
                ID #{contact.id} · created {contact.created_at ? new Date(contact.created_at).toLocaleDateString() : "—"}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <Section title="Identity">
            <Grid2>
              <Input label="Phone *" value={form.phone || ""} onChange={(v) => setField("phone", v)} placeholder="9876543210" />
              <Input label="Country Code *" value={form.country_code || ""} onChange={(v) => setField("country_code", v)} placeholder="91" />
              <Input label="Full Name" value={form.full_name || ""} onChange={(v) => setField("full_name", v)} />
              <Input label="Email" type="email" value={form.email || ""} onChange={(v) => setField("email", v)} />
              <Input label="First Name" value={form.first_name || ""} onChange={(v) => setField("first_name", v)} />
              <Input label="Last Name" value={form.last_name || ""} onChange={(v) => setField("last_name", v)} />
              <Input label="Company" value={form.company_name || ""} onChange={(v) => setField("company_name", v)} />
              <Input label="Designation" value={form.designation || ""} onChange={(v) => setField("designation", v)} />
            </Grid2>
          </Section>

          <Section title="Address">
            <Grid2>
              <Input label="City" value={form.city || ""} onChange={(v) => setField("city", v)} />
              <Input label="State" value={form.state || ""} onChange={(v) => setField("state", v)} />
              <Input label="Country" value={form.country || ""} onChange={(v) => setField("country", v)} />
              <Input label="Pincode" value={form.pincode || ""} onChange={(v) => setField("pincode", v)} />
            </Grid2>
            <Input
              label="Address line"
              value={form.address || ""}
              onChange={(v) => setField("address", v)}
              multiline
            />
          </Section>

          <Section title="Other">
            <Grid2>
              <Select
                label="Gender"
                value={form.gender || ""}
                onChange={(v) => setField("gender", v)}
                options={[
                  { value: "", label: "—" },
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                  { value: "other", label: "Other" },
                ]}
              />
              <Input label="Date of birth" type="date" value={form.date_of_birth || ""} onChange={(v) => setField("date_of_birth", v)} />
              <Input label="Tags (comma-separated)" value={Array.isArray(form.tags) ? form.tags.join(", ") : (form.tags || "")} onChange={(v) => setField("tags", v)} />
              <Select
                label="Status"
                value={form.status || "active"}
                onChange={(v) => setField("status", v)}
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                  { value: "blocked", label: "Blocked" },
                ]}
              />
            </Grid2>
          </Section>

          {customFields.length > 0 && (
            <Section title="Custom fields">
              <Grid2>
                {customFields.map((cf) => (
                  <Input
                    key={cf.id}
                    label={`${cf.label}${cf.is_required ? " *" : ""}`}
                    type={cf.field_type === "number" ? "number" : cf.field_type === "date" ? "date" : "text"}
                    value={form.custom_fields?.[cf.field_key] ?? ""}
                    onChange={(v) => setCustom(cf.field_key, v)}
                  />
                ))}
              </Grid2>
            </Section>
          )}
        </form>

        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          {isEdit ? (
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-rose-700 border border-rose-200 rounded-md hover:bg-rose-50"
            >
              <Trash2 size={14} /> Delete
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} type="button" className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60"
            >
              <Save size={14} /> {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

function normalize(c) {
  const out = { ...(c || {}) };
  if (out.tags && Array.isArray(out.tags)) out.tags = out.tags.join(", ");
  if (typeof out.custom_fields === "string") {
    try { out.custom_fields = JSON.parse(out.custom_fields); } catch (_) { out.custom_fields = {}; }
  } else if (!out.custom_fields) {
    out.custom_fields = {};
  }
  return out;
}

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{title}</p>
    <div className="space-y-3">{children}</div>
  </div>
);

const Grid2 = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
);

const Input = ({ label, value, onChange, type = "text", placeholder, multiline = false }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600">{label}</span>
    {multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder={placeholder}
        className="mt-1 w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
      />
    )}
  </label>
);

const Select = ({ label, value, onChange, options }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full text-sm border border-gray-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </label>
);

export default ContactDrawer;
